import React from 'react';
import {Box, Divider, Stack} from "@mui/material";
import {useParams} from "react-router";
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import previewTheme from './previewTheme';
import VersionSelector from './VersionSelector';
import ReferencesList from './ReferencesList';
import useVersionManagement from './useVersionManagement';
import useReferenceProcessing from './useReferenceProcessing';
import DisclosureContent from './DisclosureContent';
import {ErrorState, LoadingState, NoContentState, NoVersionState} from './PreviewStates';
import UserMentionPopover from '../../../components/prosemirror-editor/components/UserMentionPopover';
import HashtagPopover from '../../../components/prosemirror-editor/components/HashtagPopover';
import {useUserMentionPopover} from '../../../components/prosemirror-editor/hooks/useUserMentionPopover';
import {useHashtagPopover} from '../../../components/prosemirror-editor/hooks/useHashtagPopover';

/**
 * DisclosurePreviewPage - Separate preview component for disclosures with its own document header
 * Uses disclosure-specific metadata display and header format
 */
const DisclosurePreviewPage = (props) => {
    const params = useParams();
    const reportId = props?.reportId || params?.reportId;
    const hideVersionSidebar = props?.hideVersionSidebar || false;
    const showVersionSidebar = !hideVersionSidebar;

    // State for selected author (for highlighting)
    const [selectedAuthorId, setSelectedAuthorId] = React.useState(null);

    // User mention popover management
    const {
        anchorEl: mentionAnchorEl,
        anchorPosition: mentionAnchorPosition,
        userData: mentionUserData,
        open: mentionPopoverOpen,
        handleUserMentionClick,
        handleClose: handleMentionPopoverClose,
    } = useUserMentionPopover();

    // Hashtag popover management
    const {
        anchorEl: hashtagAnchorEl,
        anchorPosition: hashtagAnchorPosition,
        tagData: hashtagData,
        open: hashtagPopoverOpen,
        handleHashtagClick,
        handleClose: handleHashtagPopoverClose,
    } = useHashtagPopover();

    // Manage version selection and document loading
    // When embedded (hideVersionSidebar), manage version locally instead of from URL
    const {
        version,
        latestVersion,
        versions,
        reportDoc,
        headerDoc,
        content,
        references,
        meta,
        isLoading,
        error,
        docLoading,
        versionsLoading
    } = useVersionManagement(reportId, hideVersionSidebar);


    // Process references for numbering and organization
    const {
        localReferences,
        referenceNumbers,
        referencesByMarkId,
        getReferenceNumber
    } = useReferenceProcessing(content, references);

    // Handle author selection for highlighting
    const handleAuthorClick = (authorId) => {
        // Toggle selection: if already selected, deselect
        setSelectedAuthorId(prev => prev === authorId ? null : authorId);
    };

    return (
        <>
            <Stack direction="row" sx={{
                flexGrow: 1, overflow: 'hidden'
            }}>
                {showVersionSidebar && (
                    <VersionSelector
                        reportId={reportId}
                        versions={versions}
                        latestVersion={latestVersion}
                        loading={docLoading || versionsLoading}
                    />
                )}

                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    overflowY: 'auto',
                    bgcolor: 'white'
                }}>
                    <ThemeProvider theme={previewTheme}>
                        <CssBaseline/>
                        <Stack
                            className="preview-root"
                            spacing={2}
                            p={2}
                            maxWidth={'800px'}
                            width={'100%'}
                            margin="0 auto"
                        >
                            {/* Loading state */}
                            {isLoading && <LoadingState/>}

                            {/* Error state */}
                            {error && !isLoading && <ErrorState error={error}/>}

                            {/* No version selected yet (waiting for redirect) */}
                            {!version && !isLoading && !error && (
                                <NoVersionState latestVersion={latestVersion}/>
                            )}

                            {/* No content available */}
                            {version && !isLoading && !error && !content && (
                                <NoContentState version={version}/>
                            )}

                            {/* Render disclosure document content */}
                            {!isLoading && !error && content && (
                                <DisclosureContent
                                    content={content}
                                    meta={meta}
                                    reportDoc={headerDoc || reportDoc}
                                    version={version}
                                    latestVersion={latestVersion}
                                    referencesByMarkId={referencesByMarkId}
                                    referenceNumbers={referenceNumbers}
                                    getReferenceNumber={getReferenceNumber}
                                    selectedAuthorId={selectedAuthorId}
                                    onAuthorClick={handleAuthorClick}
                                    onUserMentionClick={handleUserMentionClick}
                                    onHashtagClick={handleHashtagClick}
                                />
                            )}

                            {/* References list */}
                            {!isLoading && !error && content && (
                                <>
                                    <Divider/>
                                    <ReferencesList
                                        references={localReferences}
                                        getReferenceNumber={getReferenceNumber}
                                    />
                                </>
                            )}
                        </Stack>
                    </ThemeProvider>
                </Box>
            </Stack>

            {/* User mention popover */}
            <UserMentionPopover
                open={mentionPopoverOpen}
                anchorEl={mentionAnchorEl}
                anchorPosition={mentionAnchorPosition}
                onClose={handleMentionPopoverClose}
                userData={mentionUserData}
            />

            {/* Hashtag popover */}
            <HashtagPopover
                open={hashtagPopoverOpen}
                anchorEl={hashtagAnchorEl}
                anchorPosition={hashtagAnchorPosition}
                onClose={handleHashtagPopoverClose}
                tagData={hashtagData}
            />
        </>
    );
};

export default DisclosurePreviewPage;
